import { formatTripCounter } from "../../services/tripDisplay";
import React, { useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { useGetDockDwellBreakdownQuery } from "../../api/vmsSlice";
import { analyticsQueryArgs } from "./analyticsFilters";

/**
 * Where a truck's time on site goes, stage by stage, averaged over the window.
 *
 * Time on site starts at the Queue when the vehicle queued and at the Gate when it did not — the same
 * span the Recent Operations table reports as turnaround, so the two panels agree by construction.
 *
 * Every stage is named and grouped by the server, using the trip timeline's own vocabulary and its
 * five ribbon kinds. Nothing here holds a copy of those words: a supervisor reading this bar and an
 * operator reading a trip's timeline must not be able to drift apart.
 *
 * Nothing on this panel ticks. Time on site has no value until the vehicle has left, so the panel
 * measures completed trips only and there is no running interval to advance between polls — unlike
 * DockUtilisation above it, whose bars keep rising while a Bay is loaded.
 */
const DWELL_POLL_MS = 300000;

/**
 * The trip timeline's ribbon colours, from DockOperations.css:733-742.
 *
 * Taken rather than chosen: the drawer already paints a queue segment teal and a dock operation
 * green, and a stage bar using different colours for the same categories would be a second visual
 * language for one set of facts. It also settles the palette question the four-segment version had to
 * work around — these are already clear of DockUtilisation's band colours on the tab above.
 */
const KIND_COLORS = {
	queue: "#0f766e",
	gate: "var(--primary-color)",
	transit: "#607d8b",
	bay: "#e49a1b",
	operation: "#2e7d32",
};
// Two stages of one kind — Transit to Bay and Transit to Gate Out, Unloading and Loading — take the
// kind's colour in descending shades, so the grouping stays legible without inventing a sixth hue.
const KIND_SHADES = [100, 68, 44];

// The timeline renders a checkpoint visit shorter than this as an instant rather than a span
// ( DockTripOperations.jsx, instantGate ). A Gate visit is checked in and out in one pass, so both
// gate stages are usually a few seconds; the same rule keeps them a hairline here.
const INSTANT_STAGE_MS = 1000;
// Below this a segment is invisible, so it is floored rather than silently disappearing.
const MIN_SEGMENT_PERCENT = 0.6;
const OPERATION_LABELS = { LOADING: "Loading only", UNLOADING: "Unloading only", BOTH: "Loading and unloading" };

// Why a trip could not be measured, in the words the coverage list uses.
const EXCLUSION_REASONS = {
	noGateSpan: "entry or exit record is incomplete (the trip may still be on site)",
	noBayVisit: "reached no bay with a recorded visit",
	openVisit: "no recorded Bay departure",
	noDoorPair: "no paired door open and close",
	inconsistent: "timestamps out of order",
	unsupportedMovement: "checkpoint type not supported by this breakdown",
};

function stageColor(stage, shadeIndex) {
	const base = KIND_COLORS[stage.kind] || "var(--gray-color)";
	const shade = KIND_SHADES[Math.min(shadeIndex, KIND_SHADES.length - 1)];
	return shade === 100 ? base : `color-mix(in srgb, ${base} ${shade}%, #ffffff)`;
}

// Each stage's position within its own kind, so the shade follows the published order rather than a
// hardcoded table that would go stale the moment a stage is added.
function shadeIndexes(stages) {
	const seen = {};
	return stages.map((stage) => {
		const index = seen[stage.kind] || 0;
		seen[stage.kind] = index + 1;
		return index;
	});
}

// Reads the way an operator reads a shift: "3h 35m", "26m", and "4s" for a gate read.
function formatDuration(milliseconds) {
	const value = Math.max(0, Math.round(milliseconds));
	if (value < 60000) return `${Math.round(value / 1000)}s`;
	const totalMinutes = Math.round(value / 60000);
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	return hours > 0 ? `${hours}h ${String(minutes).padStart(2, "0")}m` : `${minutes}m`;
}

function plural(count, word) {
	return `${count} ${word}${count === 1 ? "" : "s"}`;
}

function share(part, whole) {
	return whole ? Math.round((part / whole) * 100) : 0;
}

function formatUnits(count) {
	return Number(count || 0).toLocaleString("en-MY");
}

// "320 unloaded · 410 loaded", omitting a direction that moved nothing.
function unitsLine(entry) {
	const parts = [];
	if (entry?.unitsUnloaded > 0) parts.push(`${formatUnits(entry.unitsUnloaded)} unloaded`);
	if (entry?.unitsLoaded > 0) parts.push(`${formatUnits(entry.unitsLoaded)} loaded`);
	return parts.join(" · ");
}

/**
 * One stacked bar over a set of published stages or groups.
 *
 * Widths are flex-grow proportional to each value, which fills the track exactly with no width
 * arithmetic and no rounding gap at the end. No inline figures on the ten-stage bar — they collide
 * the moment one stage dominates, and one always does — so the legend is the readout and the bar is
 * the proportion. A stage under a second draws as a hairline, matching the timeline's instant rule.
 */
// Portal outside scrolling containers and let Popper keep the details inside the viewport.
function DwellSegment({ children, ...props }) {
	const content = React.Children.toArray(children);
	const details = content.pop();
	return (
		<Tooltip
			title={details}
			placement="top"
			describeChild
			enterTouchDelay={0}
			PopperProps={{ modifiers: [
				{ name: "preventOverflow", options: { rootBoundary: "viewport", padding: 8, altAxis: true } },
				{ name: "flip", options: { rootBoundary: "viewport", padding: 8 } },
			] }}
			componentsProps={{ tooltip: { sx: {
				maxWidth: "min(260px, calc(100vw - 32px))",
				maxHeight: "calc(100dvh - 32px)",
				overflowY: "auto",
				padding: "7px 9px",
				backgroundColor: "rgba(20, 20, 20, 0.94)",
			} } }}
		>
			<div tabIndex={0} {...props}>{content}</div>
		</Tooltip>
	);
}

function StageBar({ items, total, colors, showLabels = false }) {
	const label = items.filter((item) => item.meanMs > 0)
		.map((item) => `${item.label} ${formatDuration(item.meanMs)}`)
		.join(", ") + `, ${formatDuration(total)} in total`;
	return (
		<div className="dock-dwell-bar" role="img" aria-label={label}>
			{items.map((item, index) => {
				if (!(item.meanMs > 0)) return null;
				const percent = share(item.meanMs, total);
				const instant = item.meanMs < INSTANT_STAGE_MS;
				return (
					<DwellSegment
						key={item.key || item.kind}
						className={`dock-dwell-seg${instant ? " dock-dwell-seg-instant" : ""}`}
						style={{
							flex: instant
								? "0 0 3px"
								: `${Math.max(item.meanMs, (total * MIN_SEGMENT_PERCENT) / 100)} 1 0`,
							backgroundColor: colors[index],
						}}
					>
						{showLabels && percent >= 8 && <span className="dock-dwell-seg-label">{item.label} {formatDuration(item.meanMs)}</span>}
						<span className="dock-dwell-tooltip">
							<strong>{item.label} · {formatDuration(item.meanMs)} · {percent}%</strong>
						</span>
					</DwellSegment>
				);
			})}
		</div>
	);
}

// One Bay's row. Widths are against the slowest dock rather than a fixed axis, because these bars
// exist to be compared with each other and none of them is still moving.
function DockRow({ row, slowestMs, stages, colors }) {
	const label = `${row.dock}, ${formatDuration(row.meanVisitMs)} average per visit across ${plural(row.visits, "visit")}`;
	return (
		<div className="dock-dwell-row">
			<span className="dock-dwell-row-bay">{row.dock}</span>
			<div className="dock-dwell-row-track">
				<div
					className="dock-dwell-row-bar"
					role="img"
					aria-label={label}
					style={{ width: `${slowestMs ? (row.meanVisitMs / slowestMs) * 100 : 0}%` }}
				>
					{stages.map((stage, index) => (row.mean[stage.key] > 0 ? (
						<DwellSegment
							key={stage.key}
							className="dock-dwell-seg"
							style={{ flex: `${row.mean[stage.key]} 1 0`, backgroundColor: colors[index] }}
						>
							<span className="dock-dwell-tooltip">
								<strong>
									{row.dock} · {stage.label} · {formatDuration(row.mean[stage.key])}
								</strong>
								<em>Averaged over this bay&apos;s {plural(row.visits, "visit")}.</em>
							</span>
						</DwellSegment>
					) : null))}
				</div>
			</div>
			<span className="dock-dwell-row-total">{row.visits ? formatDuration(row.meanVisitMs) : "—"}</span>
		</div>
	);
}

export default function DwellBreakdown({ plantId, canView, filters }) {
	// The day and the Bay all come from the toolbar above, which filters this panel and
	// the utilisation panel together: read side by side, they must be answering about the same day.
	const { date, selectedBay } = filters;
	// Sort stays local: it reorders rows this panel already has, it changes no request, and the
	// utilisation panel has no row order to keep in step with.
	const [sortBy, setSortBy] = useState("dock");

	const query = useGetDockDwellBreakdownQuery(
		analyticsQueryArgs({ plantId, date, selectedBay }),
		{ skip: !plantId || !canView, pollingInterval: DWELL_POLL_MS },
	);
	const response = query.currentData;
	const data = response?.data;

	const facility = data?.facility;
	const stages = facility?.stages || [];
	const groups = facility?.groups || [];
	const coverage = data?.coverage;
	const rows = data?.rows || [];
	const measured = Number(facility?.trips) || 0;
	const totalMs = Number(facility?.meanSiteMs) || 0;

	const stageColors = useMemo(() => {
		const indexes = shadeIndexes(stages);
		return stages.map((stage, index) => stageColor(stage, indexes[index]));
	}, [stages]);
	const groupColors = useMemo(
		() => groups.map((group) => KIND_COLORS[group.kind] || "var(--gray-color)"),
		[groups],
	);
	// The Bay rows carry only the bay-local stages, and which those are is the server's decision —
	// read off the row rather than duplicated here.
	const bayStages = useMemo(
		() => stages.filter((stage) => rows[0]?.mean
			&& Object.prototype.hasOwnProperty.call(rows[0].mean, stage.key)),
		[stages, rows],
	);
	const bayStageColors = useMemo(() => {
		const indexes = shadeIndexes(bayStages);
		return bayStages.map((stage, index) => stageColor(stage, indexes[index]));
	}, [bayStages]);

	// The bars and the legend carry figures only; what each stage covers and how many trips reached
	// it is said in the explainers and the READ captions, in sentences rather than in shorthand.
	const groupItems = groups;
	// Each stage is averaged over the trips that reached it, including instant stages.
	const stageItems = stages.map((stage) => ({
		...stage,
		meanMs: stage.presentTrips > 0
			? (stage.conditionalMeanMs ?? (stage.meanMs * measured / stage.presentTrips))
			: 0,
	}));
	const stageTotalMs = stageItems.reduce((sum, stage) => sum + stage.meanMs, 0);

	const sortedRows = useMemo(() => {
		const ordered = [...rows];
		return sortBy === "slowest"
			? ordered.sort((first, second) => second.meanVisitMs - first.meanVisitMs)
			: ordered.sort((first, second) => first.checkpointNumber - second.checkpointNumber);
	}, [rows, sortBy]);
	const slowestMs = Math.max(0, ...rows.map((row) => row.meanVisitMs));
	const byOperation = Object.keys(OPERATION_LABELS).map(operation =>
		(data?.byOperation || []).find(entry => entry.operation === operation)
		|| { operation, trips: 0, mean: {}, meanTripMs: 0 });
	const unclassifiedTrips = (data?.byOperation || []).filter(entry => !OPERATION_LABELS[entry.operation])
		.reduce((sum, entry) => sum + (entry.trips || 0), 0);
	const selectedBayName = data?.selectedBays?.length === 1 ? data.selectedBays[0].displayName : null;
	const stageFor = (key) => stages.find((stage) => stage.key === key);

	// Every figure below is measured from the numbers the panel is drawing, so the caption cannot
	// drift from the bars above it.
	const captions = [];
	if (measured > 0 && totalMs > 0) {
		captions.push(
			`Average time from Queue entry (or Gate In) to Gate Out was ${formatDuration(totalMs)},`
			+ ` across ${plural(measured, "completed trip")}.`,
		);
		// Use the same per-stage averages as the legend; do not mix in all-trip activity averages.
		for (const stage of stageItems) {
			if (!stage.presentTrips) continue;
			if (stage.meanMs <= 0) continue;
			if (stage.presentTrips < measured || ["loading", "unloading", "doorOpenToClose"].includes(stage.key)) {
				captions.push(
					`${stage.label} averaged ${formatDuration(stage.meanMs)} across ${plural(stage.presentTrips, "trip")} where it occurred.`,
				);
			}
		}
		if (facility.doorIdleMs > 0) {
			captions.push(
				`${formatDuration(facility.doorIdleMs)} per trip was spent with the door closed between door openings during a Bay visit.`,
			);
		}
		if (unitsLine(facility)) captions.push(`Units moved: ${unitsLine(facility)}.`);
		if (selectedBayName) {
			// The bar changed meaning when the chip was pressed, and it has to say so.
			captions.push(`The selected trips all used ${selectedBayName}. Trip averages still cover their full journey, including any other bays visited.`);
		}
		if (facility.inferredDoorCloses) {
			captions.push(
				`${plural(facility.inferredDoorCloses, "visit")} used Bay departure as an estimated door-close time.`,
			);
		}
		if (data?.capped) {
			captions.push("The result limit was reached. These averages cover only part of the selected period.");
		}
	}

	// Never optional: a split over 14 of 23 trips is useful, one that hides the other nine is not.
	let coverageLine = null;
	if (coverage?.consideredTrips) {
		const excluded = coverage.excluded || {};
		const reasons = [];
		if (excluded.noGateSpan) reasons.push(`${excluded.noGateSpan} without a complete entry-to-exit record (including trips still on site)`);
		if (excluded.openVisit) reasons.push(`${excluded.openVisit} with no recorded Bay departure`);
		if (excluded.noDoorPair) reasons.push(`${excluded.noDoorPair} without usable door-open and door-close records`);
		if (excluded.inconsistent) reasons.push(`${excluded.inconsistent} with timestamps out of order`);
		if (excluded.noBayVisit) reasons.push(`${excluded.noBayVisit} with no recorded Bay visit`);
		if (excluded.unsupportedMovement) reasons.push(`${excluded.unsupportedMovement} with a checkpoint type this breakdown does not support`);
		coverageLine = `Based on ${coverage.segmentedTrips} of ${plural(coverage.consideredTrips, "trip")} in this period`
			+ (reasons.length ? `. Excluded: ${reasons.join("; ")}.` : ".");
		const unsplit = (facility?.workSplitBasis?.unsplitMixed || 0)
			+ (facility?.workSplitBasis?.unsplitNoMarker || 0);
		if (unsplit > 0) {
			coverageLine += ` Loading and unloading could not be separated for ${plural(unsplit, "visit")}; that door-open time is included under ${stageFor("doorOpenToClose")?.label || "Door Open → Door Close"}.`;
		}
	}

	/**
	 * The trips left out, by name.
	 *
	 * Worth the space: "based on 2 of 3" invites exactly one question, and answering it from the
	 * database took a query and a guess. Vehicle and trip number are enough to find the trip in the
	 * operations table on the tab next door.
	 */
	const excludedTrips = coverage?.excludedTrips || [];

	if (!canView) return null;

	return (
		<section className="dock-dwell-panel" aria-label="Time on site">
			<div className="dock-peak-head">
				<div>
					<h2>Time on site</h2>
					<span>
						Queue entry or Gate In to Gate Out · {selectedBayName ? `trips that used ${selectedBayName}` : "all bays"}
						{measured ? ` · ${plural(measured, "completed trip")}` : ""}
						{data?.dateLabel ? ` · ${data.dateLabel}` : ""}
						{response?.timezone ? ` · ${response.timezone.replace("_", " ")}` : ""}
					</span>
				</div>
			</div>

			{(query.error || response?.error) && (
				<div className="dock-message error">{response?.message || "Time on site could not be loaded."}</div>
			)}

			{query.isLoading || (query.isFetching && !response) ? (
				<div className="dock-peak-placeholder">Loading time on site…</div>
			) : measured > 0 ? (
				<>
					<div className="dock-dwell-overall">
						<div className="dock-dwell-overall-head">
							Average time per trip · {formatDuration(totalMs)}
						</div>
						<StageBar items={groupItems} total={totalMs} colors={groupColors} showLabels />
						<div className="dock-dwell-axis">
							<span>arrival</span>
							<span>{formatDuration(totalMs)} total</span>
						</div>
					</div>

					<div className="dock-dwell-overall">
						<div className="dock-dwell-overall-head">Stage by stage</div>
						<div className="dock-dwell-explainer">
							Each stage is averaged only over trips where that stage happened. Trips that skipped
							the stage are excluded from its average. For example, loading time is divided by the number
							of trips that loaded. These averages use different trip counts, so their sum is not the average
							trip time. Bar percentages show each stage&apos;s share of this sum.
						</div>
						<StageBar items={stageItems} total={stageTotalMs} colors={stageColors} />
						<div className="dock-dwell-axis">
							<span>0 min</span>
							<span>{formatDuration(stageTotalMs)} sum of stage averages</span>
						</div>
						{/* The readout, because a ten-stage bar cannot label itself. A stage no trip
						    reached is greyed rather than dropped: that it did not happen is information. */}
						<div className="dock-dwell-legend">
							{stageItems.map((stage, index) => (
								<span
									className={`dock-dwell-legend-item${stage.presentTrips ? "" : " dock-dwell-legend-absent"}`}
									key={stage.key}
								>
									<span className="dock-dwell-dot" style={{ backgroundColor: stageColors[index] }} />
									<span className="dock-dwell-legend-name">{stage.label}</span>
									{stage.presentTrips ? (
										<em>{formatDuration(stage.meanMs)}</em>
									) : (
										<small>not seen on this day</small>
									)}
								</span>
							))}
						</div>
					</div>

					{captions.length > 0 && (
						<div className="dock-dwell-insight">
							<span className="dock-dwell-tag">Summary</span>
							{captions.join(" ")}
						</div>
					)}

					<div className="dock-dwell-twocol">
						<div className="dock-dwell-block">
							<div className="dock-dwell-block-head">
								<h3>By dock</h3>
								<div className="dock-filter-chips" role="group" aria-label="Sort docks">
									<button type="button" className="dock-chip" aria-pressed={sortBy === "dock"} onClick={() => setSortBy("dock")}>
										Dock order
									</button>
									<button type="button" className="dock-chip" aria-pressed={sortBy === "slowest"} onClick={() => setSortBy("slowest")}>
										Slowest first
									</button>
								</div>
							</div>
							{sortedRows.map((row) => (
								<DockRow
									key={row.checkpointNumber}
									row={row}
									slowestMs={slowestMs}
									stages={bayStages}
									colors={bayStageColors}
								/>
							))}
							{/* Trip-level stages belong to no bay, so these rows are not the whole story and
							    must not be read as if they added up to the bar above. */}
							<div className="dock-dwell-note">
								Each dock shows average time per measured visit, from transit to that bay through Bay departure.
								Each stage is averaged over all measured visits to that dock, including visits without that activity. Queue time, gate processing and transit to Gate Out are excluded.
							</div>
						</div>

						<div className="dock-dwell-block">
							<div className="dock-dwell-block-head"><h3>Trips by loading and unloading activity</h3></div>
							{byOperation.length ? byOperation.map((entry) => (
								<div className="dock-dwell-operation" key={entry.operation || "unreported"}>
									<div className="dock-dwell-overall-head">
										{OPERATION_LABELS[entry.operation] || "Direction not reported"}
										{" · "}{plural(entry.trips, "trip")}
										{unitsLine(entry) ? ` · ${unitsLine(entry)}` : ""}
									</div>
									{entry.trips === 0 ? <div className="dock-dwell-note">No trips in this group.</div> : <StageBar
										items={stages.map((stage) => ({
											...stage,
											meanMs: entry.mean[stage.key] || 0,
										}))}
										total={entry.meanTripMs}
										colors={stageColors}
									/>}
									<div className="dock-dwell-axis">
										<span>0 min</span>
										<span>{formatDuration(entry.meanTripMs)} per trip</span>
									</div>
								</div>
							)) : (
								<div className="dock-dwell-note">No trip reported which way its goods moved.</div>
							)}
							{/* Read from activity across the entire trip rather than from a receiving/shipping
							    face, which the checkpoint model does not carry. */}
							<div className="dock-dwell-note">
								Each trip belongs to one group based on loading and unloading across all its bay visits.
								Each bar averages all trips in that group, from Queue entry or Gate In to Gate Out. A trip can load and unload at different bays and still belong to the Loading and unloading group.
								{unclassifiedTrips > 0 && ` ${plural(unclassifiedTrips, "trip")} had no attributed loading or unloading time and are not shown in these groups.`}
							</div>
						</div>
					</div>

					{coverageLine && (
						<div className="dock-dwell-coverage">
							{coverageLine}
							{excludedTrips.length > 0 && (
								<details className="dock-dwell-excluded">
									<summary>Which trips were left out</summary>
									<ul>
										{excludedTrips.map((trip) => (
											<li key={trip.tripId}>
												{trip.vehicleNumber || "Unknown vehicle"}
												{trip.tripCounter ? ` · trip ${formatTripCounter(trip.tripCounter)}` : ""}
												{" — "}{EXCLUSION_REASONS[trip.reason] || trip.reason}
											</li>
										))}
									</ul>
									{coverage.excludedTripsCapped && <p>Only the first {excludedTrips.length} are listed.</p>}
								</details>
							)}
						</div>
					)}
				</>
			) : (
				<div className="dock-dwell-empty">
					{selectedBayName
						? `No completed trips through ${selectedBayName} have enough recorded events for this breakdown.`
						: "No completed trips have enough recorded events to calculate time on site for this period."}
					{coverageLine ? ` ${coverageLine}` : ""}
				</div>
			)}
		</section>
	);
}
